import { html, nothing, type TemplateResult } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { toSanitizedMarkdownHtml } from "../markdown.ts";

/**
 * Token threshold above which a long assistant message is auto-collapsed
 * behind a TL;DR preview. Approximate — we use a char-based heuristic that
 * matches the spec's "messages over ~800 tokens" without a real tokenizer.
 *
 * 1 token ≈ 4 chars; 800 tokens ≈ 3200 chars.
 */
export const LONG_MESSAGE_CHAR_THRESHOLD = 3200;

/** Length of the inline TL;DR preview. */
const TLDR_PREVIEW_MAX_CHARS = 280;

/** Length of the one-line collapsed preview shown when the user manually
 *  collapses an otherwise short message. */
const COLLAPSED_PREVIEW_MAX_CHARS = 120;

/**
 * Section headings that mark a message as an operator-facing final answer.
 * When a long assistant message contains one of these headings, we surface
 * the actionable line in the TL;DR instead of the first paragraph, and we
 * bypass the long-message auto-collapse so the operator never has to click
 * "Show full reasoning" to read the verdict / next step.
 *
 * Three heading forms are accepted:
 *   - Markdown heading:  `## Status`, `### Next step`, `# Verdict`
 *   - Bold paragraph:    `**Status**`, `**TL;DR**`, `**Recommendation**`
 *   - Both with the same prose body underneath
 *
 * Matching is case-insensitive. The keyword list is intentionally narrow —
 * these are the headings the agent has been using consistently for its
 * own operator-facing final answers (see the chat UX runbook).
 */
const OPERATOR_FINAL_ANSWER_HEADING_RE =
  /^\s{0,3}(?:#{1,6}\s+|\*\*)(?:Status|Result|Verdict|Recommendation|Recommended|Recommended next step|Next step|Action|Next actions|What to do next|Summary|Conclusion|TL;DR|Answer)\b[^\n]*$/im;

/**
 * Capture the body under one of the recognised final-answer section
 * headings. Body extends until the next heading (markdown or bold) or
 * end of string.
 */
const OPERATOR_FINAL_ANSWER_SECTION_RE =
  /^\s{0,3}(?:#{1,6}\s+|\*\*)(Status|Result|Verdict|Recommendation|Recommended|Recommended next step|Next step|Action|Next actions|What to do next|Summary|Conclusion|TL;DR|Answer)\b[^\n]*\n+([\s\S]*?)(?=^\s{0,3}(?:#{1,6}\s+|\*\*)|\Z)/gim;

/**
 * Priority order for final-answer sections. Action-oriented keywords
 * (Next step, Recommendation, Action) win over status / summary
 * keywords, so the operator sees the "what to do" line in the TL;DR
 * rather than the headline. Tie-break is the first occurrence in
 * the body.
 */
const OPERATOR_FINAL_ANSWER_SECTION_PRIORITY: readonly string[] = [
  "Recommendation",
  "Recommended",
  "Recommended next step",
  "Next step",
  "Action",
  "Next actions",
  "What to do next",
  "Verdict",
  "Result",
  "Status",
  "Answer",
  "Conclusion",
  "Summary",
  "TL;DR",
];

export type ExpandableMessageBodyProps = {
  /** The raw markdown text of the message body. */
  markdown: string;
  /**
   * Force the message to render fully expanded (e.g. when the user clicks
   * "Show full reasoning" inside the TL;DR). When `true`, the TL;DR wrapper
   * is bypassed even for long messages.
   */
  forceExpanded?: boolean;
  /**
   * Manually collapsed by the user (per-message collapse action). When
   * `true`, the message renders as a one-line preview, regardless of length.
   */
  manuallyCollapsed?: boolean;
  /** Extra class on the outer wrapper. */
  className?: string;
  /** Stable message id for the viewer modal. */
  messageId?: string;
  /** Opens the full-screen message viewer for this body. */
  onOpenViewer?: (markdown: string, messageId: string) => void;
};

/**
 * Heuristic token estimate. Good enough for "should I show a TL;DR?".
 * Uses chars/4 as the proxy — same as the rest of the renderer.
 */
export function estimateTokens(markdown: string): number {
  if (!markdown) return 0;
  return Math.ceil(markdown.length / 4);
}

/**
 * Detect operator-facing final answers: long assistant messages that
 * contain a recognisable "Status / Verdict / Next step / Action" section
 * heading. These messages ARE the answer — collapsing them behind
 * "Show full reasoning" makes the operator hunt for a click before
 * they can act, which is the exact bug JAISIU-synthesis-render is fixing.
 *
 * Returns `false` for empty input and for messages that lack one of the
 * recognised headings; in those cases the caller falls back to the
 * normal first-paragraph TL;DR.
 */
export function isOperatorFinalAnswer(markdown: string): boolean {
  const body = (markdown ?? "").trim();
  if (!body) return false;
  return OPERATOR_FINAL_ANSWER_HEADING_RE.test(body);
}

/**
 * Strip a single leading bullet marker (`-`, `*`, `+`, or `1.`) from a
 * line and return the cleaned text. Returns the original string when no
 * marker is present.
 */
function stripLeadingListMarker(line: string): string {
  return line
    .replace(/^[-*+]\s+/, "")
    .replace(/^\d+\.\s+/, "")
    .trim();
}

/**
 * Pull the first non-heading, non-empty bullet / numbered list item out
 * of the markdown under one of the recognised final-answer section
 * headings. Falls back to the first non-empty paragraph when no such
 * bullet exists, mirroring the original `buildTldrPreview` behaviour so
 * unrecognised long messages still get a useful preview.
 *
 * When the body contains more than one recognised section, the highest-
 * priority section wins (see OPERATOR_FINAL_ANSWER_SECTION_PRIORITY) so
 * the TL;DR surfaces the operator's "what to do next" line rather than
 * a generic "Status: ✅ all done" headline.
 */
function pickActionableLine(markdown: string, maxLen: number): string {
  const body = (markdown ?? "").trim();
  if (!body) return "";
  // Collect every recognised section in document order, then pick the
  // one whose keyword has the highest priority. First-occurrence breaks
  // ties inside the same priority band.
  const candidates: { keyword: string; body: string; order: number }[] = [];
  const sectionMatches = body.matchAll(OPERATOR_FINAL_ANSWER_SECTION_RE);
  let order = 0;
  for (const match of sectionMatches) {
    candidates.push({
      keyword: match[1] ?? "",
      body: match[2] ?? "",
      order: order++,
    });
  }
  if (candidates.length > 0) {
    const priorityIndex = (kw: string) => {
      const idx = OPERATOR_FINAL_ANSWER_SECTION_PRIORITY.findIndex(
        (k) => k.toLowerCase() === kw.toLowerCase(),
      );
      return idx === -1 ? Number.POSITIVE_INFINITY : idx;
    };
    candidates.sort((a, b) => {
      const pa = priorityIndex(a.keyword);
      const pb = priorityIndex(b.keyword);
      if (pa !== pb) return pa - pb;
      return a.order - b.order;
    });
    const best = candidates[0];
    const lines = best.body
      .split(/\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    for (const line of lines) {
      if (/^#{1,6}\s+/.test(line)) break; // next heading — section ended
      if (/^\*\*[^*]+\*\*$/.test(line)) break; // next bold heading — section ended
      const cleaned = stripLeadingListMarker(line);
      if (cleaned.length > 0) {
        if (cleaned.length <= maxLen) return cleaned;
        return `${cleaned.slice(0, maxLen - 1).trimEnd()}…`;
      }
    }
  }
  // Fall back to the first non-empty paragraph (existing behaviour).
  const paragraphs = body.split(/\n\s*\n/);
  for (const p of paragraphs) {
    const lines = p
      .split(/\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) continue;
    if (lines.length === 1 && /^#{1,6}\s+/.test(lines[0])) continue;
    const joined = lines
      .map((l) =>
        l
          .replace(/^#{1,6}\s+/, "")
          .replace(/^[-*+]\s+/, "")
          .replace(/^\d+\.\s+/, ""),
      )
      .join(" ")
      .trim();
    if (joined.length > 0) {
      if (joined.length <= maxLen) return joined;
      return `${joined.slice(0, maxLen - 1).trimEnd()}…`;
    }
  }
  // Last resort: first non-empty line, no truncation handling.
  const firstLine = body
    .split(/\n/)
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  if (!firstLine) return "";
  if (firstLine.length <= maxLen) return firstLine;
  return `${firstLine.slice(0, maxLen - 1).trimEnd()}…`;
}

/**
 * Pick the first non-empty paragraph of the message as a TL;DR preview.
 * Strips leading heading markers / list bullets and HTML-escapes nothing —
 * the result is plain text for a `<span>`.
 *
 * If the message qualifies as an operator-facing final answer (see
 * `isOperatorFinalAnswer`), prefer the actionable line under the
 * final-answer heading so the operator sees the verdict / next step
 * without expanding.
 */
export function buildTldrPreview(markdown: string, maxLen = TLDR_PREVIEW_MAX_CHARS): string {
  const body = (markdown ?? "").trim();
  if (!body) return "";
  if (isOperatorFinalAnswer(body)) {
    return pickActionableLine(body, maxLen);
  }
  // Walk paragraphs (double newline separated) and pick the first paragraph
  // that has real body content. A paragraph is "heading-only" if its only
  // non-empty line starts with a `#` marker — we skip those so the TL;DR
  // surfaces actual prose, not just a title.
  const paragraphs = body.split(/\n\s*\n/);
  let first = "";
  for (const p of paragraphs) {
    const lines = p
      .split(/\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) continue;
    if (lines.length === 1 && /^#{1,6}\s+/.test(lines[0])) continue;
    const joined = lines
      .map((l) =>
        l
          .replace(/^#{1,6}\s+/, "")
          .replace(/^[-*+]\s+/, "")
          .replace(/^\d+\.\s+/, ""),
      )
      .join(" ")
      .trim();
    if (joined.length > 0) {
      first = joined;
      break;
    }
  }
  if (!first) first = body;
  if (first.length <= maxLen) return first;
  return `${first.slice(0, maxLen - 1).trimEnd()}…`;
}

/**
 * One-line preview used when a user manually collapses a message via the
 * per-message collapse action.
 */
/** Plain-text preview: drop common inline markdown markers. */
function stripInlineMarkdownForPreview(text: string): string {
  return text
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_{1,2}([^_]+)_{1,2}/g, "$1");
}

export function buildCollapsedPreview(
  markdown: string,
  maxLen = COLLAPSED_PREVIEW_MAX_CHARS,
): string {
  return stripInlineMarkdownForPreview(buildTldrPreview(markdown, maxLen));
}

/**
 * Decide whether the message qualifies for the auto TL;DR + collapsible
 * wrapper. Returns `true` when the body is long enough to warrant a
 * summary, AND the user has not manually expanded it.
 */
export function isLongMessage(markdown: string): boolean {
  if (!markdown) return false;
  if (markdown.length < LONG_MESSAGE_CHAR_THRESHOLD) return false;
  return estimateTokens(markdown) >= 800;
}

/**
 * Render the body of an assistant / user message with the long-message
 * auto-collapse + TL;DR wrapper.
 *
 * Behaviour:
 *  - Short messages: render the markdown directly (no wrapper).
 *  - Long messages (>=800 tokens): show a TL;DR preview + a "Show full
 *    reasoning" button that, when clicked, expands the full body.
 *  - Manually collapsed messages: show a one-line ellipsis preview, no
 *    body. A "Show message" button restores it.
 *
 * The collapse/expand state for the auto path is local to the message
 * bubble — the parent does not need to track it. Use the `forceExpanded`
 * prop when the parent owns the expand state externally.
 */
export function renderExpandableMessageBody(
  props: ExpandableMessageBodyProps,
): TemplateResult | typeof nothing {
  const markdown = props.markdown ?? "";
  if (!markdown.trim()) {
    return nothing;
  }

  // Manual collapse wins — show the one-line preview, no body.
  if (props.manuallyCollapsed === true) {
    const preview = buildCollapsedPreview(markdown);
    return html`
      <div
        class=${["chat-message-expand chat-message-expand--manual", props.className]
          .filter(Boolean)
          .join(" ")}
        data-testid="chat-message-manual-collapse"
      >
        <div class="chat-message-expand__manual-preview">
          <span class="chat-message-expand__chevron" aria-hidden="true">▸</span>
          <span class="chat-message-expand__preview-text muted" data-testid="chat-collapsed-preview"
            >${preview || "Message collapsed"}</span
          >
        </div>
      </div>
    `;
  }

  // Short messages: render normally.
  //
  // Operator-facing final answers (Status / Next step / Recommendation /
  // etc.) are NEVER auto-collapsed, even when they are long. The whole
  // point of the message is to deliver a verdict and a recommended next
  // action — wrapping it in "Show full reasoning" makes the operator
  // click before they can act, which is the exact bug the synthesis
  // rendering fix is closing. `forceExpanded` and `manuallyCollapsed`
  // still take precedence; this branch only widens the "short message"
  // path for recognised final-answer bodies.
  if (!isLongMessage(markdown) || props.forceExpanded === true || isOperatorFinalAnswer(markdown)) {
    return html`
      <div
        class=${["chat-message-expand", props.className].filter(Boolean).join(" ")}
        data-testid="chat-message-body"
      >
        <div class="chat-text">${unsafeHTML(toSanitizedMarkdownHtml(markdown))}</div>
      </div>
    `;
  }

  // Long message: TL;DR + collapsible full body, both rendered so the
  // transition is instant on toggle. The `<details>` element owns the
  // open/closed state — no parent re-render needed.
  const tldr = buildTldrPreview(markdown);
  const approxTokens = estimateTokens(markdown);
  return html`
    <div
      class=${["chat-message-expand chat-message-expand--long", props.className]
        .filter(Boolean)
        .join(" ")}
      data-testid="chat-message-long"
    >
      <div class="chat-message-expand__tldr" aria-label="Message summary">
        <span class="chat-message-expand__tldr-label">TL;DR</span>
        <span class="chat-message-expand__tldr-body">${tldr}</span>
      </div>
      ${
        props.onOpenViewer && props.messageId
          ? html`
              <button
                type="button"
                class="btn btn--sm chat-message-expand__open-viewer"
                data-testid="chat-collapsed-open-viewer"
                title="Open in viewer"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  props.onOpenViewer!(markdown, props.messageId!);
                }}
              >
                Open in viewer
              </button>
            `
          : nothing
      }
      <details
        class="chat-message-expand__details"
        data-testid="chat-message-expand-details"
        @toggle=${(e: Event) => {
          // Surface the open state to anyone who cares (telemetry / tests).
          const d = e.currentTarget as HTMLDetailsElement | null;
          if (!d) return;
          d.dataset.userOpened = d.open ? "1" : "0";
        }}
      >
        <summary class="chat-message-expand__summary">
          <span class="chat-message-expand__chevron" aria-hidden="true">▸</span>
          <span class="chat-message-expand__summary-text">Show full reasoning</span>
          <span class="chat-message-expand__meta muted"
            >~${approxTokens.toLocaleString()} tokens</span
          >
        </summary>
        <div class="chat-message-expand__body chat-text">
          ${unsafeHTML(toSanitizedMarkdownHtml(markdown))}
        </div>
      </details>
    </div>
  `;
}
